/**
 * Home-Screen / installed-app detection for PWA safe-area floors.
 *
 * iOS often reports `navigator.standalone === true` while the CSS
 * `@media (display-mode: standalone)` query is still false — especially
 * on a cold launch of an old Add-to-Home-Screen install. The install
 * prompt already knew this; chrome CSS did not, so `--vs-dashboard-safe-top`
 * stayed at `env(safe-area-inset-top)` (0 when viewport-fit=cover never
 * reached the installed webview) and the Dynamic Island clipped the
 * top bar. `minimal-ui` is in `display_override` and must count too.
 */
export const VS_STANDALONE_ATTR = "data-vs-standalone";

/**
 * Pixel floor when env() is 0 or the compact 20px status-bar lie
 * (iOS 26.1 WebKit #301994). iPhone 16/17 Pro islands report 62px;
 * 3.75rem (60px) sat 2px into that cutout. Use px, not rem, so html
 * font-size cannot shrink the floor.
 */
export const STANDALONE_SAFE_TOP_FLOOR_PX = 62;
export const STANDALONE_SAFE_TOP_FLOOR = `${STANDALONE_SAFE_TOP_FLOOR_PX}px`;

/** Compact / pre-notch status bar; also the iOS 26.1 false inset. */
export const COMPACT_STATUS_BAR_PX = 20;

/** Smallest real notch inset (iPhone X). Values below this are not islands. */
export const REAL_NOTCH_MIN_PX = 44;

/**
 * Extra pixels above the island floor when classifying the iOS 26
 * home-screen lie (`screen.height − viewport` in 44–80px). Safari
 * toolbars are hundreds of pixels and must not match.
 */
export const IOS26_VIEWPORT_LIE_SLACK_PX = 18;

export const STANDALONE_DISPLAY_MODE_QUERIES = [
  "(display-mode: standalone)",
  "(display-mode: fullscreen)",
  "(display-mode: minimal-ui)",
] as const;

export function isStandaloneDisplayMode(
  win: Window = typeof window === "undefined" ? (undefined as unknown as Window) : window,
): boolean {
  if (!win) return false;
  const nav = win.navigator as Navigator & { standalone?: boolean };
  if (nav.standalone === true) return true;
  if (typeof win.matchMedia !== "function") return false;
  return STANDALONE_DISPLAY_MODE_QUERIES.some(
    (query) => win.matchMedia(query).matches,
  );
}

export function syncStandaloneDisplayMode(
  root: HTMLElement,
  win: Window = typeof window === "undefined" ? (undefined as unknown as Window) : window,
): boolean {
  const standalone = isStandaloneDisplayMode(win);
  if (standalone) {
    root.setAttribute(VS_STANDALONE_ATTR, "1");
  } else {
    root.removeAttribute(VS_STANDALONE_ATTR);
  }
  return standalone;
}
